/**
 * RecommendationProvenance — the reusable "show your work + cite + not advice"
 * affordance required by docs/METHODS-AND-COMPLIANCE-FOUNDATION.md.
 *
 * Every figure/recommendation that asserts something (verdict, gap-closer, a
 * ratio, a tax lever) renders this so the output is VALID + CITED + defensible
 * as a tool, not advice:
 *   - inputs/assumptions shown ("how this is calculated")
 *   - authority cited inline (method note or IRC §/standard)
 *   - "not advice" micro-disclaimer
 *   - deterministic + explainable; the user decides (HITL)
 *
 * Collapsed by default (an "i" affordance) so it informs without cluttering.
 */
import { useState } from 'react';
import { Info } from 'lucide-react';

interface ProvenanceProps {
  /** Short title, e.g. "How this is calculated". */
  title?: string;
  /** The formula / inputs / assumptions lines. */
  lines: string[];
  /** Optional authority/source label, e.g. "IRC §6654" or "tax-basis, unaudited". */
  cite?: string;
  /** Optional "not advice" note; defaults to the standard one. Pass null to omit. */
  disclaimer?: string | null;
  className?: string;
}

const DEFAULT_DISCLAIMER =
  'Informational decision-support, not tax, legal, or financial advice. Confirm with your CPA.';

export function RecommendationProvenance({
  title = 'How this is calculated',
  lines,
  cite,
  disclaimer = DEFAULT_DISCLAIMER,
  className = '',
}: ProvenanceProps) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`mt-2 ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid="provenance-toggle"
        className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
      >
        <Info className="w-3.5 h-3.5" />
        {title}
      </button>
      {open && (
        <div
          data-testid="provenance-body"
          className="mt-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 px-3 py-2 text-[11px] leading-relaxed text-slate-600 dark:text-slate-300 space-y-1"
        >
          {lines.map((l, i) => (
            <p key={i}>{l}</p>
          ))}
          {cite && (
            <p className="font-mono text-[10px] text-primary-700 dark:text-primary-300">{cite}</p>
          )}
          {disclaimer && (
            <p className="text-[10px] text-slate-400 dark:text-slate-500 pt-1 border-t border-slate-200 dark:border-slate-700">
              {disclaimer}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * BasisBadge — the honest basis-of-accounting label for any financial figure
 * (never "GAAP"). e.g. <BasisBadge basis="tax" /> → "Tax basis · unaudited".
 */
export function BasisBadge({
  basis = 'tax',
  className = '',
}: {
  basis?: 'tax' | 'cash' | 'accrual';
  className?: string;
}) {
  const label =
    basis === 'cash' ? 'Cash basis · unaudited' : basis === 'accrual' ? 'Accrual · unaudited' : 'Tax basis · unaudited';
  return (
    <span
      data-testid="basis-badge"
      title="Management financials on a special-purpose basis. Not a CPA compilation, review, or audit; no assurance provided."
      className={`inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-[10px] font-semibold px-2 py-0.5 ${className}`}
    >
      {label}
    </span>
  );
}
